import React, { useState, useEffect } from "react";

const flattenObject = (obj, prefix = "") =>
    Object.entries(obj).reduce((acc, [key, val]) => {
        const newKey = prefix ? `${prefix}.${key}` : key;
        if (val && typeof val === "object" && !Array.isArray(val)) {
            Object.assign(acc, flattenObject(val, newKey));
        } else {
            acc[newKey] = val;
        }
        return acc;
    }, {});

const TableWithSettings = ({ data, label }) => {
    const [flattenedData, setFlattenedData] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [selectedHeaders, setSelectedHeaders] = useState([]);

    useEffect(() => {
        if (!data || !Array.isArray(data)) return;

        const flat = data.map((item) => flattenObject(item));
        const all = Array.from(new Set(flat.flatMap((d) => Object.keys(d))));
        setFlattenedData(flat);
        setHeaders(all);
        setSelectedHeaders(all);
    }, [data]);

    const toggleHeader = (header) => {
        setSelectedHeaders((prev) =>
            prev.includes(header) ? prev.filter((h) => h !== header) : [...prev, header]
        );
    };

    if (!data || data.length === 0) return null;

    return (
        <div style={{ marginBottom: "30px" }}>
            <h4>{label}</h4>
            <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", border: "1px solid #ddd", padding: "10px" }}>
                {headers.map((header) => (
                    <label key={header}>
                        <input
                            type="checkbox"
                            checked={selectedHeaders.includes(header)}
                            onChange={() => toggleHeader(header)}
                        />{" "}
                        {header}
                    </label>
                ))}
            </div>
            <table style={{ borderCollapse: "collapse", width: "100%", marginTop: "10px" }}>
                <thead>
                    <tr>
                        {selectedHeaders.map((header) => (
                            <th key={header} style={{ border: "1px solid #ccc", padding: "8px" }}>
                                {header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {flattenedData.map((row, idx) => (
                        <tr key={idx}>
                            {selectedHeaders.map((header) => (
                                <td key={header} style={{ border: "1px solid #ccc", padding: "8px" }}>
                                    {String(row[header] ?? "")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const DataTableJson = ({ json }) => {
    const renderContent = (key, value) => {
        if (typeof value === "string" || typeof value === "number" || typeof value === "boolean") {
            return (
                <div key={key} style={{ marginBottom: "10px" }}>
                    <strong>{key}:</strong> {String(value)}
                </div>
            );
        }

        if (Array.isArray(value)) {
            if (value.length === 0) return null;
            const isObjectList = typeof value[0] === "object";
            if (isObjectList) {
                return <TableWithSettings key={key} data={value} label={key} />;
            } else {
                // Primitive array as table
                const wrapped = value.map((v) => ({ value: v }));
                return <TableWithSettings key={key} data={wrapped} label={key} />;
            }
        }

        if (typeof value === "object") {
            const nestedKeys = Object.keys(value);
            const isAllPrimitive = nestedKeys.every(
                (k) => typeof value[k] !== "object" || value[k] === null
            );
            if (isAllPrimitive) {
                return (
                    <TableWithSettings
                        key={key}
                        data={[value]}
                        label={key}
                    />
                );
            }

            return (
                <div key={key} style={{ marginLeft: "20px", marginBottom: "20px" }}>
                    <h3>{key}</h3>
                    {nestedKeys.map((subKey) => renderContent(`${key}.${subKey}`, value[subKey]))}
                </div>
            );
        }

        return null;
    };

    return (
        <div>
            {Object.entries(json).map(([key, value]) => renderContent(key, value))}
        </div>
    );
};

export default DataTableJson;
